import {
  addDays,
  addHours,
  addMilliseconds,
  addMinutes,
  addMonths,
  addSeconds,
  addWeeks,
  addYears,
  differenceInMilliseconds,
  format,
  isAfter,
  isBefore,
  isEqual,
  startOfDay,
  startOfHour,
  startOfMinute,
  startOfMonth,
  startOfSecond,
  startOfWeek,
} from "date-fns";
import {
  BrushDurationFrequencyMapping,
  BrushEventData,
  BrushTickFrequencyType,
  BrushTickValueType,
} from "./Brush.types";

import { Typography } from "@/components/ui/typography";

// Double-checked and corrected FREQUENCY_DURATIONS values for each BrushTickFrequencyType.
// All values are in milliseconds.

export const FREQUENCY_DURATIONS = Object.entries({
  year: 1000 * 60 * 60 * 24 * 365, // 1 year (approx, not accounting for leap years)
  month: 1000 * 60 * 60 * 24 * 30, // 1 month (approx, 30 days)
  "half-month": 1000 * 60 * 60 * 24 * 15, // 15 days
  week: 1000 * 60 * 60 * 24 * 7, // 7 days
  day: 1000 * 60 * 60 * 24, // 24 hours
  "half-day": 1000 * 60 * 60 * 12, // 12 hours
  "quarter-day": 1000 * 60 * 60 * 6, // 6 hours
  hour: 1000 * 60 * 60, // 60 minutes
  "half-hour": 1000 * 60 * 30, // 30 minutes
  "quarter-hour": 1000 * 60 * 15, // 15 minutes
  minute: 1000 * 60, // 60 seconds
  "half-minute": 1000 * 30, // 30 seconds
  "quarter-minute": 1000 * 15, // 15 seconds
  second: 1000, // 1 second
  "half-second": 500, // 0.5 second
  "quarter-second": 250, // 0.25 second
  decisecond: 100, // 0.1 second
  "half-decisecond": 50, // 0.05 second
  millisecond: 1, // 1 ms
}).sort((a, b) => b[1] - a[1]);

export const DURATION_MARKS = {
  "2s": 1000 * 2,
  "5s": 1000 * 5,
  "10s": 1000 * 10,
  "15s": 1000 * 15,
  "30s": 1000 * 30,
  "1m": 1000 * 60,
  "2m": 1000 * 60 * 2,
  "5m": 1000 * 60 * 5,
  "10m": 1000 * 60 * 10,
  "15m": 1000 * 60 * 15,
  "30m": 1000 * 60 * 30,
  "1h": 1000 * 60 * 60,
  "2h": 1000 * 60 * 60 * 2,
  "4h": 1000 * 60 * 60 * 4,
  "8h": 1000 * 60 * 60 * 8,
  "12h": 1000 * 60 * 60 * 12,
  "1d": 1000 * 60 * 60 * 24,
  "2d": 1000 * 60 * 60 * 24 * 2,
  "3d": 1000 * 60 * 60 * 24 * 3,
  "1w": 1000 * 60 * 60 * 24 * 7,
  "2w": 1000 * 60 * 60 * 24 * 14,
  "1mo": 1000 * 60 * 60 * 24 * 30,
  "2mo": 1000 * 60 * 60 * 24 * 60,
  "3mo": 1000 * 60 * 60 * 24 * 90,
  "6mo": 1000 * 60 * 60 * 24 * 180,
  "1y": 1000 * 60 * 60 * 24 * 365,
  "2y": 1000 * 60 * 60 * 24 * 365 * 2,
  "3y": 1000 * 60 * 60 * 24 * 365 * 3,
};

const getFrequencyFromDuration = (
  duration: number,
  minTicks: number = 20,
  maxTicks: number = 56,
): BrushTickFrequencyType => {
  for (const [frequency, freqDuration] of FREQUENCY_DURATIONS) {
    const numTicks = Math.floor(duration / freqDuration);
    if (numTicks >= minTicks && numTicks <= maxTicks) {
      return frequency as BrushTickFrequencyType;
    }
  }
  // Fallback: return the smallest frequency
  return FREQUENCY_DURATIONS[0][0] as BrushTickFrequencyType;
};

// prettier-ignore
export const DURATION_FREQUENCY_MAPPING: BrushDurationFrequencyMapping[] = [
  // Sub-second range
  { duration: DURATION_MARKS["2s"], label: "2s", frequency: getFrequencyFromDuration(DURATION_MARKS["2s"]) },
  { duration: DURATION_MARKS["5s"], label: "5s", frequency: getFrequencyFromDuration(DURATION_MARKS["5s"]) },
  { duration: DURATION_MARKS["10s"], label: "10s", frequency: getFrequencyFromDuration(DURATION_MARKS["10s"]) },
  { duration: DURATION_MARKS["15s"], label: "15s", frequency: getFrequencyFromDuration(DURATION_MARKS["15s"]) },
  { duration: DURATION_MARKS["30s"], label: "30s", frequency: getFrequencyFromDuration(DURATION_MARKS["30s"]) },
  { duration: DURATION_MARKS["1m"], label: "1m", frequency: getFrequencyFromDuration(DURATION_MARKS["1m"]) },
  { duration: DURATION_MARKS["2m"], label: "2m", frequency: getFrequencyFromDuration(DURATION_MARKS["2m"]) },
  { duration: DURATION_MARKS["5m"], label: "5m", frequency: getFrequencyFromDuration(DURATION_MARKS["5m"]) },
  { duration: DURATION_MARKS["10m"], label: "10m", frequency: getFrequencyFromDuration(DURATION_MARKS["10m"]) },
  { duration: DURATION_MARKS["30m"], label: "30m", frequency: getFrequencyFromDuration(DURATION_MARKS["30m"]) },
  { duration: DURATION_MARKS["1h"], label: "1h", frequency: getFrequencyFromDuration(DURATION_MARKS["1h"]) },
  { duration: DURATION_MARKS["2h"], label: "2h", frequency: getFrequencyFromDuration(DURATION_MARKS["2h"]) },
  { duration: DURATION_MARKS["4h"], label: "4h", frequency: getFrequencyFromDuration(DURATION_MARKS["4h"]) },
  { duration: DURATION_MARKS["8h"], label: "8h", frequency: getFrequencyFromDuration(DURATION_MARKS["8h"]) },
  { duration: DURATION_MARKS["12h"], label: "12h", frequency: getFrequencyFromDuration(DURATION_MARKS["12h"]) },
  { duration: DURATION_MARKS["1d"], label: "1d", frequency: getFrequencyFromDuration(DURATION_MARKS["1d"]) },
  { duration: DURATION_MARKS["2d"], label: "2d", frequency: getFrequencyFromDuration(DURATION_MARKS["2d"]) },
  { duration: DURATION_MARKS["3d"], label: "3d", frequency: getFrequencyFromDuration(DURATION_MARKS["3d"]) },
  { duration: DURATION_MARKS["1w"], label: "1w", frequency: getFrequencyFromDuration(DURATION_MARKS["1w"]) },
  { duration: DURATION_MARKS["2w"], label: "2w", frequency: getFrequencyFromDuration(DURATION_MARKS["2w"]) },
  { duration: DURATION_MARKS["1mo"], label: "1mo", frequency: getFrequencyFromDuration(DURATION_MARKS["1mo"]) },
  { duration: DURATION_MARKS["2mo"], label: "2mo", frequency: getFrequencyFromDuration(DURATION_MARKS["2mo"]) },
  { duration: DURATION_MARKS["3mo"], label: "3mo", frequency: getFrequencyFromDuration(DURATION_MARKS["3mo"]) },
  { duration: DURATION_MARKS["6mo"], label: "6mo", frequency: getFrequencyFromDuration(DURATION_MARKS["6mo"]) },
  { duration: DURATION_MARKS["1y"], label: "1y", frequency: getFrequencyFromDuration(DURATION_MARKS["1y"]) },
  { duration: DURATION_MARKS["2y"], label: "2y", frequency: getFrequencyFromDuration(DURATION_MARKS["2y"]) },
  { duration: DURATION_MARKS["3y"], label: "3y", frequency: getFrequencyFromDuration(DURATION_MARKS["3y"]) },
];

export const getTickValues = (
  startT: number,
  endT: number,
  maxTicks: number = 100,
): BrushTickValueType[] => {
  const startDate = new Date(startT);
  const endDate = new Date(endT);
  const frequency = getOptimalFrequency((endT - startT) / 1000);
  console.log("frequency", frequency);

  // Helper to snap a date to the next frequency boundary using date-fns
  const snapToNextBoundary = (
    date: Date,
    freq: BrushTickFrequencyType,
  ): Date => {
    switch (freq) {
      case "year": {
        const nextYear = addYears(startOfMonth(date), 12);
        return startOfDay(nextYear);
      }
      case "month": {
        const nextMonth = addMonths(startOfMonth(date), 1);
        return startOfDay(nextMonth);
      }
      case "half-month": {
        const nextHalfMonth = addMonths(startOfMonth(date), 1);
        return startOfDay(nextHalfMonth);
      }
      case "week": {
        const nextWeek = addWeeks(startOfWeek(date, { weekStartsOn: 1 }), 1);
        return startOfDay(nextWeek);
      }
      case "day": {
        const nextDay = addDays(startOfDay(date), 1);
        return startOfDay(nextDay);
      }
      case "half-day": {
        const currentHour = date.getHours();
        if (currentHour < 12) {
          const noon = new Date(date);
          noon.setHours(12, 0, 0, 0);
          return noon;
        } else {
          return startOfDay(addDays(date, 1));
        }
      }
      case "quarter-day": {
        const currentHour = date.getHours();
        const quarter = Math.floor(currentHour / 6);
        const nextQuarter = new Date(date);
        nextQuarter.setHours((quarter + 1) * 6, 0, 0, 0);
        return nextQuarter;
      }
      case "hour": {
        const nextHour = addHours(startOfHour(date), 1);
        return startOfHour(nextHour);
      }
      case "half-hour": {
        const currentMinute = date.getMinutes();
        const halfHour = Math.floor(currentMinute / 30);
        const nextHalfHour = new Date(date);
        nextHalfHour.setMinutes((halfHour + 1) * 30, 0, 0);
        return nextHalfHour;
      }
      case "quarter-hour": {
        const currentMinute = date.getMinutes();
        const quarter = Math.floor(currentMinute / 15);
        const nextQuarter = new Date(date);
        nextQuarter.setMinutes((quarter + 1) * 15, 0, 0);
        return nextQuarter;
      }
      case "minute": {
        const nextMinute = addMinutes(startOfMinute(date), 1);
        return startOfMinute(nextMinute);
      }
      case "half-minute": {
        const currentSecond = date.getSeconds();
        const halfMinute = Math.floor(currentSecond / 30);
        const nextHalfMinute = new Date(date);
        nextHalfMinute.setSeconds((halfMinute + 1) * 30, 0);
        return nextHalfMinute;
      }
      case "quarter-minute": {
        const currentSecond = date.getSeconds();
        const quarter = Math.floor(currentSecond / 15);
        const nextQuarter = new Date(date);
        nextQuarter.setSeconds((quarter + 1) * 15, 0);
        return nextQuarter;
      }
      case "second": {
        const nextSecond = addSeconds(startOfSecond(date), 1);
        return startOfSecond(nextSecond);
      }
      case "half-second": {
        const currentMs = date.getMilliseconds();
        const halfSecond = Math.floor(currentMs / 500);
        const nextHalfSecond = new Date(date);
        nextHalfSecond.setMilliseconds((halfSecond + 1) * 500);
        return nextHalfSecond;
      }
      case "quarter-second": {
        const currentMs = date.getMilliseconds();
        const quarter = Math.floor(currentMs / 250);
        const nextQuarter = new Date(date);
        nextQuarter.setMilliseconds((quarter + 1) * 250);
        return nextQuarter;
      }
      case "decisecond": {
        const currentMs = date.getMilliseconds();
        const decisecond = Math.floor(currentMs / 100);
        const nextDecisecond = new Date(date);
        nextDecisecond.setMilliseconds((decisecond + 1) * 100);
        return nextDecisecond;
      }
      case "half-decisecond": {
        const currentMs = date.getMilliseconds();
        const halfDecisecond = Math.floor(currentMs / 50);
        const nextHalfDecisecond = new Date(date);
        nextHalfDecisecond.setMilliseconds((halfDecisecond + 1) * 50);
        return nextHalfDecisecond;
      }
      case "millisecond": {
        return addMilliseconds(date, 1);
      }
      default:
        throw new Error(`Unknown frequency: ${freq as string}`);
    }
  };

  // Helper to add time by frequency using date-fns
  const add = (
    date: Date,
    freq: BrushTickFrequencyType,
    step: number = 1,
  ): Date => {
    switch (freq) {
      case "year":
        return addYears(date, step);
      case "month":
        return addMonths(date, step);
      case "half-month":
        return addMonths(date, step / 2);
      case "week":
        return addWeeks(date, step);
      case "day":
        return addDays(date, step);
      case "half-day":
        return addHours(date, 12 * step);
      case "quarter-day":
        return addHours(date, 6 * step);
      case "hour":
        return addHours(date, step);
      case "half-hour":
        return addMinutes(date, 30 * step);
      case "quarter-hour":
        return addMinutes(date, 15 * step);
      case "minute":
        return addMinutes(date, step);
      case "half-minute":
        return addSeconds(date, 30 * step);
      case "quarter-minute":
        return addSeconds(date, 15 * step);
      case "second":
        return addSeconds(date, step);
      case "half-second":
        return addMilliseconds(date, 500 * step);
      case "quarter-second":
        return addMilliseconds(date, 250 * step);
      case "decisecond":
        return addMilliseconds(date, 100 * step);
      case "half-decisecond":
        return addMilliseconds(date, 50 * step);
      case "millisecond":
        return addMilliseconds(date, step);
      default:
        throw new Error(`Unknown frequency: ${freq as string}`);
    }
  };

  const totalDuration = differenceInMilliseconds(endDate, startDate);
  if (totalDuration <= 0) return [];

  const ticks: BrushTickValueType[] = [];
  const startTime = startDate.getTime();

  // Find the first frequency boundary that's >= startDate
  let current = snapToNextBoundary(startDate, frequency);

  // If the first boundary is after endDate, we still need to include startDate and endDate
  if (isAfter(current, endDate)) {
    // // Add startDate as first tick
    // ticks.push({
    //   timestamp: startTime,
    //   offset: 0, // startTime - startTime = 0
    //   percentage: 1, // 100%
    // });

    // // Add endDate as last tick
    // const endTime = endDate.getTime();
    // ticks.push({
    //   timestamp: endTime,
    //   offset: 1, // 100%
    //   percentage: 0, // No duration for the end tick
    // });

    return ticks;
  }

  // Add all frequency boundary ticks
  let i = 0;
  while (
    (isBefore(current, endDate) || isEqual(current, endDate)) &&
    ticks.length < maxTicks
  ) {
    const currentTime = current.getTime();
    const offset = (currentTime - startTime) / totalDuration; // Normalize to 0-1

    // Calculate the duration this tick represents
    const nextTick = add(current, frequency, 1);
    const tickDuration = Math.min(
      differenceInMilliseconds(nextTick, current),
      differenceInMilliseconds(endDate, current),
    );
    const percentage = tickDuration / totalDuration; // Percentage based on actual time duration

    ticks.push({
      timestamp: currentTime,
      offset,
      percentage,
    });

    current = add(current, frequency, 1);
    i++;
    // Safety: prevent infinite loop
    if (i > 10000) break;
  }

  return ticks;
};

// Helper function to format tick timestamps based on frequency
export const formatTickTime = (
  timestamp: number,
  frequency: BrushTickFrequencyType,
): string => {
  const date = new Date(timestamp);

  switch (frequency) {
    case "year":
      return format(date, "yyyy");
    case "month":
      return format(date, "MMM yyyy");
    case "half-month":
      return format(date, "MMM d");
    case "week":
      return format(date, "MMM d");
    case "day":
      return format(date, "MMM d");
    case "half-day":
      return format(date, "h:mm a");
    case "quarter-day":
      return format(date, "h:mm a");
    case "hour":
      return format(date, "h:mm a");
    case "half-hour":
      return format(date, "h:mm a");
    case "quarter-hour":
      return format(date, "h:mm a");
    case "minute":
      return format(date, "h:mm:ss");
    case "half-minute":
      return format(date, "h:mm:ss");
    case "quarter-minute":
      return format(date, "h:mm:ss");
    case "second":
      return format(date, "h:mm:ss");
    case "half-second":
      return format(date, "h:mm:ss.S");
    case "quarter-second":
      return format(date, "h:mm:ss.S");
    case "decisecond":
      return format(date, "h:mm:ss.S");
    case "half-decisecond":
      return format(date, "h:mm:ss.SS");
    case "millisecond":
      return format(date, "h:mm:ss.SSS");
    default:
      return format(date, "MMM d, h:mm a");
  }
};

// Helper function to format delta in human-readable format
export const formatDelta = (deltaSeconds: number): string => {
  const absDelta = Math.abs(deltaSeconds);
  const sign = deltaSeconds >= 0 ? "+" : "-";

  if (absDelta < 1) {
    // Less than 1 second - show milliseconds
    const milliseconds = Math.round(absDelta * 1000);
    return `${sign}${milliseconds}ms`;
  } else if (absDelta < 60) {
    // Less than 1 minute - show seconds with 1 decimal
    return `${sign}${absDelta.toFixed(1)}s`;
  } else if (absDelta < 3600) {
    // Less than 1 hour - show minutes and seconds
    const minutes = Math.floor(absDelta / 60);
    const seconds = Math.floor(absDelta % 60);
    return `${sign}${minutes}m ${seconds}s`;
  } else if (absDelta < 86400) {
    // Less than 1 day - show hours and minutes
    const hours = Math.floor(absDelta / 3600);
    const minutes = Math.floor((absDelta % 3600) / 60);
    return `${sign}${hours}h ${minutes}m`;
  } else if (absDelta < 2592000) {
    // Less than 1 month - show days and hours
    const days = Math.floor(absDelta / 86400);
    const hours = Math.floor((absDelta % 86400) / 3600);
    return `${sign}${days}d ${hours}h`;
  } else if (absDelta < 31536000) {
    // Less than 1 year - show months and days
    const months = Math.floor(absDelta / 2592000);
    const days = Math.floor((absDelta % 2592000) / 86400);
    return `${sign}${months}mo ${days}d`;
  } else {
    // 1 year or more - show years and months
    const years = Math.floor(absDelta / 31536000);
    const months = Math.floor((absDelta % 31536000) / 2592000);
    return `${sign}${years}y ${months}mo`;
  }
};

export const getIndexOfNextEvent = (
  timestamp: number,
  events: BrushEventData[],
): number => {
  let left = 0,
    right = events.length;
  while (left < right) {
    const mid = (left + right) >> 1;
    if (events[mid].timestamp > timestamp) right = mid;
    else left = mid + 1;
  }
  return left;
};

export const getIndexOfPreviousEvent = (
  timestamp: number,
  events: BrushEventData[],
): number => {
  let left = 0,
    right = events.length;
  while (left < right) {
    const mid = (left + right) >> 1;
    if (events[mid].timestamp < timestamp) left = mid + 1;
    else right = mid;
  }
  return left - 1;
};

// Get optimal frequency for any duration
export const getOptimalFrequency = (
  durationSeconds: number,
): BrushTickFrequencyType => {
  // console.error(DURATION_FREQUENCY_MAPPING);
  const duration = durationSeconds * 1000;
  // Find the appropriate frequency based on duration thresholds
  for (let i = 0; i < DURATION_FREQUENCY_MAPPING.length; i++) {
    if (duration < DURATION_FREQUENCY_MAPPING[i].duration) {
      if (i === 0) {
        return DURATION_FREQUENCY_MAPPING[0].frequency;
      }
      return DURATION_FREQUENCY_MAPPING[i - 1].frequency;
    }
  }
  // If duration is greater than all, return the last frequency
  return DURATION_FREQUENCY_MAPPING[DURATION_FREQUENCY_MAPPING.length - 1]
    .frequency;
};

// Get slider marks for the duration slider - LINEAR MAPPING
// Get the duration range for the slider - LINEAR MAPPING
export const getDurationSliderRange = () => {
  return {
    min: 0, // Start at index 0
    max: DURATION_FREQUENCY_MAPPING.length - 1, // End at last index
  };
};

// Convert linear slider index to actual duration in seconds, allowing for interpolation on a log scale
export const getDurationFromSliderIndex = (index: number): number => {
  // Clamp index to valid range
  const clampedIndex = Math.max(
    0,
    Math.min(index, DURATION_FREQUENCY_MAPPING.length - 1),
  );

  const lowerIndex = Math.floor(clampedIndex);
  const upperIndex = Math.ceil(clampedIndex);

  // If index is exactly an integer, just return the mapped duration
  if (lowerIndex === upperIndex) {
    return DURATION_FREQUENCY_MAPPING[lowerIndex].duration;
  }

  // Get the two durations to interpolate between
  const lowerDuration = DURATION_FREQUENCY_MAPPING[lowerIndex].duration;
  const upperDuration = DURATION_FREQUENCY_MAPPING[upperIndex].duration;

  // Calculate the fraction between the two indices
  const t = clampedIndex - lowerIndex;

  // Interpolate on a log scale
  // log10(lower) + t * (log10(upper) - log10(lower))
  const logLower = Math.log10(lowerDuration);
  const logUpper = Math.log10(upperDuration);
  const interpolatedLog = logLower + t * (logUpper - logLower);
  const interpolatedDuration = Math.pow(10, interpolatedLog);

  return interpolatedDuration;
};

// Convert duration in seconds to linear slider index
export const getSliderIndexFromDuration = (durationSeconds: number): number => {
  const durationMs = durationSeconds * 1000;

  // Find the closest mapping
  let closestIndex = 0;
  let minDistance = Math.abs(
    durationMs - DURATION_FREQUENCY_MAPPING[0].duration,
  );

  for (let i = 1; i < DURATION_FREQUENCY_MAPPING.length; i++) {
    const distance = Math.abs(
      durationMs - DURATION_FREQUENCY_MAPPING[i].duration,
    );
    if (distance < minDistance) {
      minDistance = distance;
      closestIndex = i;
    }
  }

  return closestIndex;
};

// Function that takes a row object and returns event data
export const transformRowToEvent = (row: any): BrushEventData => {
  return {
    timestamp: row.timestamp || Date.now(),
    value: row.value || Math.floor(Math.random() * 100),
    label: row.label || `Event ${Math.floor(Math.random() * 1000)}`,
  };
};

// Generate 100 random data points with half before and half after current time
export const generateRandomEventData = (): BrushEventData[] => {
  const currentTime = Date.now();
  const thirtySeconds = 30 * 1000; // 30 seconds in milliseconds
  const events: BrushEventData[] = [];

  // Generate 50 events in the past (30 seconds before current time)
  for (let i = 0; i < 50; i++) {
    const randomOffset = Math.random() * thirtySeconds; // Random offset within 30 seconds
    const timestamp = currentTime - thirtySeconds + randomOffset;

    events.push({
      timestamp,
      value: Math.floor(Math.random() * 100),
      label: `Past Event ${i + 1}`,
    });
  }

  // Generate 50 events in the future (30 seconds after current time)
  for (let i = 0; i < 50; i++) {
    const randomOffset = Math.random() * thirtySeconds; // Random offset within 30 seconds
    const timestamp = currentTime + randomOffset;

    events.push({
      timestamp,
      value: Math.floor(Math.random() * 100),
      label: `Future Event ${i + 1}`,
    });
  }

  // Sort events by timestamp
  return events.sort((a, b) => a.timestamp - b.timestamp);
};

// Generate sample row data for testing
export const generateSampleRowData = () => {
  const currentTime = Date.now();
  const thirtySeconds = 30 * 1000;
  const rows = [];

  // Generate 50 past events
  for (let i = 0; i < 50; i++) {
    const randomOffset = Math.random() * thirtySeconds;
    rows.push({
      timestamp: currentTime - thirtySeconds + randomOffset,
      value: Math.floor(Math.random() * 100),
      label: `Sample Past ${i + 1}`,
    });
  }

  // Generate 50 future events
  for (let i = 0; i < 50; i++) {
    const randomOffset = Math.random() * thirtySeconds;
    rows.push({
      timestamp: currentTime + randomOffset,
      value: Math.floor(Math.random() * 100),
      label: `Sample Future ${i + 1}`,
    });
  }

  return rows.sort((a, b) => a.timestamp - b.timestamp);
};

// Get the next event after the given timestamp
export const getNextEvent = (
  events: BrushEventData[],
  currentTimestamp: number,
): BrushEventData | null => {
  const sortedEvents = events
    .filter((event) => event.timestamp > currentTimestamp)
    .sort((a, b) => a.timestamp - b.timestamp);

  return sortedEvents.length > 0 ? sortedEvents[0] : null;
};

// Get the previous event before the given timestamp
export const getPreviousEvent = (
  events: BrushEventData[],
  currentTimestamp: number,
): BrushEventData | null => {
  const sortedEvents = events
    .filter((event) => event.timestamp < currentTimestamp)
    .sort((a, b) => b.timestamp - a.timestamp);

  return sortedEvents.length > 0 ? sortedEvents[0] : null;
};

// Get the closest event to the given timestamp (next or previous)
export const getClosestEvent = (
  events: BrushEventData[],
  currentTimestamp: number,
): BrushEventData | null => {
  if (events.length === 0) return null;

  const sortedEvents = events.sort((a, b) => a.timestamp - b.timestamp);

  let closest = sortedEvents[0];
  let minDistance = Math.abs(currentTimestamp - closest.timestamp);

  for (const event of sortedEvents) {
    const distance = Math.abs(currentTimestamp - event.timestamp);
    if (distance < minDistance) {
      minDistance = distance;
      closest = event;
    }
  }

  return closest;
};
